import type { ReactNode } from 'react';
import { Badge, Meter, StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { GpuPool, HealthPools, ScaleDecision } from './health-types';
import { partName } from '../pools/pools-words';
import { capShare, decisionTone, decisionWord, poolStateTone, poolStateWord } from './health-words';

/** What the two days' cloud card-hours cost at list, when the monitor says what a card costs an hour. */
function DayCost({ pools }: { pools: HealthPools }): ReactNode {
  const f = useFormat();
  const rate = pools.dollarsPerHour;
  if (rate == null) return null;
  return (
    <div>
      <dt>Cost at list</dt>
      <dd>
        {f.money(pools.todayHours * rate)} today · {f.money(pools.yesterdayHours * rate)} yesterday
      </dd>
    </div>
  );
}

/** Today's cloud card-hours against the day's cap, and yesterday's beside it. */
function DayHours({ pools }: { pools: HealthPools }): ReactNode {
  const f = useFormat();
  const share = capShare(pools.todayHours, pools.cardHoursPerDay);
  const hours = (h: number): string => `${f.decimal(h, 1)} h`;
  return (
    <div className="hl-hours">
      <div className="hl-hours__today">
        <span className="pl-part__label">Cloud card-hours today (UTC)</span>
        {share === null ? (
          <strong>{hours(pools.todayHours)}</strong>
        ) : (
          <Meter
            value={Math.round(Math.min(1, share) * 100)}
            label="Cloud card-hours today against the day's cap"
            tone={share > 1 ? 'danger' : share >= 0.75 ? 'warning' : 'accent'}
            display={`${hours(pools.todayHours)} of ${hours(pools.cardHoursPerDay ?? 0)}`}
          />
        )}
      </div>
      <dl className="pl-summary__facts">
        <div>
          <dt>Yesterday</dt>
          <dd>{hours(pools.yesterdayHours)}</dd>
        </div>
        <DayCost pools={pools} />
        <div>
          <dt>Starts and stops</dt>
          <dd>{pools.scaleOn === false ? 'said, not done' : 'by the monitor'}</dd>
        </div>
        {pools.idleMinutes !== null ? (
          <div>
            <dt>Stops when idle</dt>
            <dd>{f.duration(pools.idleMinutes * 60)}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

/** One pool's two days as bars on a shared scale, so the busiest pool's bar is the full width. */
function HourBars({ pool, most }: { pool: GpuPool; most: number }): ReactNode {
  const f = useFormat();
  const width = (h: number): string => `${most > 0 ? Math.round((h / most) * 100) : 0}%`;
  return (
    <dl className="hl-bars">
      {(
        [
          ['Today', pool.todayHours, 'hl-bar--today'],
          ['Yesterday', pool.yesterdayHours, 'hl-bar--yesterday'],
        ] as const
      ).map(([label, h, kind]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            <span className="hl-bar">
              <span className={`hl-bar__fill ${kind}`} style={{ width: width(h) }} />
            </span>
            <span className="hl-bar__value">{f.decimal(h, 1)} h</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function PoolCard({
  pool,
  most,
  known,
}: {
  pool: GpuPool;
  most: number;
  known: boolean;
}): ReactNode {
  const f = useFormat();
  const state = poolStateWord[pool.state];
  return (
    <article className={`pl-part hl-pool hl-pool--${pool.state}`} aria-label={partName(pool.id)}>
      <header className="pl-part__head">
        <StatusDot tone={known ? poolStateTone[pool.state] : 'neutral'} label={state} />
        <h3 className="pl-part__name">{partName(pool.id)}</h3>
        <span className="pl-part__state">{state}</span>
      </header>
      <p className="pl-part__ready">
        {pool.onCards !== null ? (
          <>
            <strong>{f.int(pool.onCards)}</strong> {pool.onCards === 1 ? 'card' : 'cards'} now
          </>
        ) : (
          'cards not read'
        )}
        {pool.desktopBacked ? <span className="hl-pool__note"> · desktops first</span> : null}
      </p>
      <HourBars pool={pool} most={most} />
    </article>
  );
}

function DecisionRow({ decision, now }: { decision: ScaleDecision; now: number }): ReactNode {
  const f = useFormat();
  return (
    <li className="hl-decision">
      <time className="hl-decision__when" dateTime={decision.at} title={f.dateTime(decision.at)}>
        {f.time(decision.at)}
        <span>{f.ago(decision.at, now)}</span>
      </time>
      <Badge tone={decisionTone(decision)}>{decisionWord(decision)}</Badge>
      <p className="hl-decision__what">
        <strong>{partName(decision.id)}</strong>{' '}
        <span className="hl-decision__n">
          {f.int(decision.from)} → {f.int(decision.to)}
        </span>
        <span className="hl-decision__why">{decision.why}</span>
      </p>
    </li>
  );
}

/**
 * The cloud GPU pools the health monitor starts when work waits and stops when idle: on or off now, cards held,
 * today's and yesterday's card-hours, and every start and stop of the two days with its reason.
 */
export function GpuPoolsPanel({ pools, now }: { pools: HealthPools; now: number }): ReactNode {
  const most = Math.max(0, ...pools.list.flatMap((p) => [p.todayHours, p.yesterdayHours]));
  return (
    <>
      <DayHours pools={pools} />
      {pools.list.length === 0 ? (
        <p className="pl-part__quiet">No pool is started on demand.</p>
      ) : (
        <div className="pl-grid">
          {pools.list.map((p) => (
            <PoolCard key={p.id} pool={p} most={most} known={pools.known} />
          ))}
        </div>
      )}
      <h3 className="hl-sub">Starts and stops, today and yesterday</h3>
      {pools.decisions.length === 0 ? (
        <p className="pl-part__quiet">None.</p>
      ) : (
        <ol className="hl-decisions" aria-label="Starts and stops">
          {pools.decisions.map((d) => (
            <DecisionRow key={`${d.id}-${d.at}`} decision={d} now={now} />
          ))}
        </ol>
      )}
    </>
  );
}
